/* eslint-disable no-param-reassign, max-params */
import { api } from "jiffy-api";
import { readCookie } from "common/utils/cookie";
import { trackDeliveryDateChange } from "../utils/tracker";

const initialState = {
  estimatedDeliveryDate: null,
  regularDeliveryDate: null,
  error: null,
  errorMessages: {
    delivery: "Error getting delivery data",
    zipcode: "Error updating zipcode",
    location: "Sorry, we do not ship to this location"
  },
  shippingType: "Jiffy Made Shipping",
  zipcode: null,
  zipcodeFallback: "75201",
  zipcodeFromCookie: readCookie("address")?.split("+")?.pop(),
  outOfStockItems: []
};

const deliveryModule = {
  namespaced: true,

  state() {
    return { ...initialState };
  },

  getters: {
    estimatedDeliveryDate: state => state.estimatedDeliveryDate,
    deliveryDate: state =>
      state.regularDeliveryDate ||
      state.estimatedDeliveryDate || {
        full: null,
        timeleft: null
      },
    error: state => state.error,
    errorMessages: state => state.errorMessages,
    regularDeliveryDate: state => state.regularDeliveryDate,
    shippingType: state => state.shippingType,
    zipcode: state => state.zipcode || state.zipcodeFromCookie || state.zipcodeFallback,
    zipcodeFallback: state => state.zipcodeFallback,
    zipcodeSaved: state => state.zipcode,
    outOfStockItems: state => state.outOfStockItems
  },

  mutations: {
    raiseError(state, error) {
      state.error = error;
    },
    setEstimatedDeliveryDate(state, deliveryData) {
      state.estimatedDeliveryDate = deliveryData;
    },
    setRegularDeliveryDate(state, deliveryData) {
      state.regularDeliveryDate = deliveryData;
    },
    setZipcode(state, zipcode) {
      state.zipcode = zipcode;
    },
    setOutOfStockItems(state, items) {
      state.outOfStockItems = items;
    }
  },

  actions: {
    async getDelivery({ commit, dispatch, getters, rootGetters }, params) {
      const { compositions, placements } = rootGetters["checkout/currentAssemblyGroup"];

      if (!getters.zipcodeSaved) {
        await dispatch("updateZipcode", getters.zipcode);
      }

      if (Object.keys(compositions).length && placements.length && getters.zipcodeSaved) {
        dispatch("getRegularDelivery", params);
        return;
      }

      commit("setRegularDeliveryDate", null);
    },
    async getRegularDelivery({ commit, dispatch, getters }, params) {
      commit("loading/setDelivery", true, { root: true });

      try {
        commit("raiseError", null);
        const cb = new Date().getTime();
        const response = await api.print.delivery({ cb });
        const { delivery, shipments, items_out_of_stock: outOfStockItems } = response;

        commit("setOutOfStockItems", outOfStockItems);

        if (!delivery || !shipments) {
          const errorMessage = params?.showErrors ? getters.errorMessages.delivery : "";
          throw new Error(errorMessage);
        }

        const [orderOptions] =
          Object.values(shipments.orders).find(order => order.title === getters.shippingType)
            ?.options ?? [];

        if (delivery.delivery_dates_changed) {
          trackDeliveryDateChange(
            getters.deliveryDate.full,
            delivery.delivery_dates?.fastest?.[0]?.full_date
          );
        }

        commit("setRegularDeliveryDate", {
          full: delivery.delivery_dates?.fastest?.[0]?.full_date,
          timeleft: orderOptions.valid_until_time
        });
        commit("loading/setDelivery", false, { root: true });
      } catch (error) {
        const errorMessage = params?.showErrors ? getters.errorMessages.delivery : "";
        dispatch("handleDeliveryError", errorMessage);
        commit("setRegularDeliveryDate", null);
      }
    },
    async getEstimatedDelivery({ commit, dispatch, getters, rootGetters }, params) {
      const selectedCompositionId = rootGetters["product/selectedCompositionId"];
      commit("loading/setDelivery", true, { root: true });

      try {
        const { composition_dates: compositionDates, success } = await api.print.estimatedDelivery({
          composition_id: selectedCompositionId
        });

        if (!success) {
          const errorMessage = params?.showErrors ? getters.errorMessages.delivery : "";
          throw new Error(errorMessage);
        }

        commit("setEstimatedDeliveryDate", {
          full: compositionDates?.value_full,
          timeleft: compositionDates?.timeleft
        });
        commit("loading/setDelivery", false, { root: true });
      } catch (error) {
        const errorMessage = params?.showErrors ? getters.errorMessages.delivery : "";
        dispatch("handleDeliveryError", errorMessage);
        commit("setEstimatedDeliveryDate", null);
      }
    },
    handleDeliveryError({ commit }, errorMessage) {
      commit("raiseError", errorMessage);
      commit("loading/setDelivery", false, { root: true });
    },
    async updateZipcode({ commit, dispatch, getters }, zipcode) {
      commit("loading/setDelivery", true, { root: true });
      commit("setZipcode", zipcode);

      try {
        const {
          error,
          message: errorMessage,
          zipcode: newZipcode
        } = await api.location.update({
          zipcode
        });
        if (!error && newZipcode) {
          commit("loading/setDelivery", false, { root: true });
          commit("raiseError", null);
          commit("setZipcode", newZipcode);
          dispatch("getDelivery", { showErrors: true });
        }
        if (!errorMessage) return;
        if (error === "state_not_supported") {
          dispatch("handleDeliveryError", getters.errorMessages.location);
        } else {
          dispatch("handleDeliveryError", errorMessage);
        }
        commit("setRegularDeliveryDate", null);
      } catch (error) {
        dispatch("handleDeliveryError", getters.errorMessages.zipcode);
        commit("setRegularDeliveryDate", null);
      }
    }
  }
};

export default deliveryModule;
